import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Trophy,
  RotateCcw,
  Clock,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import type { QuizConfig, QuizQuestion } from '@/components/pathways/QuizBuilder';
import { ModuleCompletionCard } from '@/components/pathways/ModuleCompletionCard';

export default function QuizModulePage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [searchParams] = useSearchParams();
  const pathwayId = searchParams.get('pathway');
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number | number[] | string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showExplanations, setShowExplanations] = useState(false);
  const [completionData, setCompletionData] = useState<{
    nextModule?: any; totalModules: number; modulesCompleted: number; isPathwayComplete: boolean; pathwayId: string;
  } | null>(null);

  // Fetch profile
  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('auth_user_id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Fetch module details
  const { data: module, isLoading: moduleLoading } = useQuery({
    queryKey: ['quiz-module', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pathway_modules')
        .select('*, learning_pathways(name)')
        .eq('id', moduleId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!moduleId
  });

  // Parse quiz config and apply shuffling
  const quizConfig: QuizConfig | null = useMemo(() => {
    if (!module?.module_config) return null;
    try {
      const config = module.module_config as unknown as QuizConfig;
      if (config && config.questions && Array.isArray(config.questions)) {
        return config;
      }
      return null;
    } catch {
      return null;
    }
  }, [module]);

  // Shuffled questions (stable for the session)
  const shuffledQuestions = useMemo(() => {
    if (!quizConfig) return [];
    
    let questions = [...quizConfig.questions];
    
    // Shuffle questions if enabled
    if (quizConfig.shuffle_questions) {
      questions = questions.sort(() => Math.random() - 0.5);
    }
    
    // Shuffle options if enabled (but keep track of correct answer)
    if (quizConfig.shuffle_options) {
      questions = questions.map(q => {
        if (q.question_type === 'true_false' || q.question_type === 'free_text') {
          // Don't shuffle True/False or Free Text options
          return q;
        }
        
        // Create array of option indices
        const optionIndices = q.options.map((_, idx) => idx);
        const shuffledIndices = [...optionIndices].sort(() => Math.random() - 0.5);
        
        // Remap options and correct answer(s)
        const newOptions = shuffledIndices.map(idx => q.options[idx]);
        
        let newCorrectAnswer: number | number[];
        if (Array.isArray(q.correct_answer)) {
          // Multi-select: remap all correct answers
          newCorrectAnswer = q.correct_answer.map(
            origIdx => shuffledIndices.findIndex(newIdx => newIdx === origIdx)
          );
        } else {
          // Single answer: remap the correct answer
          newCorrectAnswer = shuffledIndices.findIndex(idx => idx === q.correct_answer);
        }
        
        return {
          ...q,
          options: newOptions,
          correct_answer: newCorrectAnswer,
        };
      });
    }
    
    return questions;
  }, [quizConfig]);

  // Current question
  const currentQuestion = shuffledQuestions[currentQuestionIndex];
  const totalQuestions = shuffledQuestions.length;
  
  // Calculate total points
  const totalPoints = useMemo(() => {
    return shuffledQuestions.reduce((sum, q) => sum + (q.points || 1), 0);
  }, [shuffledQuestions]);

  // Calculate results with points-based scoring
  const results = useMemo(() => {
    if (!quizConfig || !isSubmitted || shuffledQuestions.length === 0) return null;

    let earnedPoints = 0;
    const correctCount = { correct: 0, total: shuffledQuestions.length };

    shuffledQuestions.forEach((q) => {
      const qPoints = q.points || 1;
      const userAnswer = answers[q.id];
      
      if (q.question_type === 'free_text') {
        // Free text: award full points for any non-empty response
        const textAnswer = typeof userAnswer === 'string' ? userAnswer.trim() : '';
        if (textAnswer.length > 0) {
          earnedPoints += qPoints;
          correctCount.correct++;
        }
      } else if (q.question_type === 'multi_select') {
        // Multi-select: full points only if all correct answers selected (and no wrong ones)
        const correctAnswers = Array.isArray(q.correct_answer) ? q.correct_answer : [q.correct_answer];
        const userAnswers = Array.isArray(userAnswer) ? userAnswer : [];

        const allCorrect = correctAnswers.every(a => userAnswers.includes(a));
        const noExtras = userAnswers.every(a => correctAnswers.includes(a));
        const isCorrect = allCorrect && noExtras && userAnswers.length > 0;

        if (isCorrect) {
          earnedPoints += qPoints;
          correctCount.correct++;
        }
      } else {
        // Single answer questions
        const correctAnswer = Array.isArray(q.correct_answer) ? q.correct_answer[0] : q.correct_answer;
        const isCorrect = userAnswer === correctAnswer;

        if (isCorrect) {
          earnedPoints += qPoints;
          correctCount.correct++;
        }
      }
    });

    const score = Math.round((earnedPoints / totalPoints) * 100);
    const passed = score >= quizConfig.passing_score;

    return { 
      earnedPoints, 
      totalPoints, 
      score, 
      passed, 
      correct: correctCount.correct, 
      total: correctCount.total 
    };
  }, [quizConfig, answers, isSubmitted, shuffledQuestions, totalPoints]);

  // Submit quiz mutation with progression logic
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!results || !profile?.id || !pathwayId || !moduleId) return;

      // Get enrollment - use maybeSingle to avoid 406
      const { data: enrollment, error: enrollError } = await supabase
        .from('user_pathway_enrollments')
        .select('id, modules_completed')
        .eq('pathway_id', pathwayId)
        .eq('user_id', profile.id)
        .maybeSingle();

      if (enrollError) throw enrollError;
      if (!enrollment) throw new Error('Not enrolled in pathway');

      // Update module progress
      const { error } = await supabase
        .from('module_progress')
        .update({
          status: 'completed',
          best_score: results.score,
          attempts: 1,
          completed_at: new Date().toISOString()
        })
        .eq('enrollment_id', enrollment.id)
        .eq('module_id', moduleId);

      if (error) throw error;

      // === PROGRESSION LOGIC ===
      // Get all modules to find the next one (include details for completion card)
      const { data: allModules } = await supabase
        .from('pathway_modules')
        .select('id, sequence_order, title, module_type, estimated_minutes, simulation_agent_id')
        .eq('pathway_id', pathwayId)
        .order('sequence_order');

      let nextModuleInfo = null;
      let totalModules = 0;
      let newModulesCompleted = (enrollment.modules_completed || 0) + 1;
      let isPathwayComplete = false;

      if (allModules && allModules.length > 0) {
        totalModules = allModules.length;
        const currentModuleIndex = allModules.findIndex(m => m.id === moduleId);
        const nextModule = allModules[currentModuleIndex + 1];
        isPathwayComplete = !nextModule;

        if (nextModule) {
          nextModuleInfo = nextModule;
        }

        // Update enrollment
        await supabase
          .from('user_pathway_enrollments')
          .update({
            modules_completed: newModulesCompleted,
            current_module_id: nextModule?.id || null,
            status: isPathwayComplete ? 'completed' : 'in_progress',
            completed_at: isPathwayComplete ? new Date().toISOString() : null
          })
          .eq('id', enrollment.id);

        // Unlock next module if exists
        if (nextModule) {
          await supabase
            .from('module_progress')
            .update({ status: 'unlocked' })
            .eq('enrollment_id', enrollment.id)
            .eq('module_id', nextModule.id);
        }

      }

      // Log quiz activity
      const { logQuizCompleted } = await import('@/lib/gamification/activityService');
      await logQuizCompleted(
        profile.id,
        module?.title || 'Quiz',
        results.score,
        results.totalPoints,
        results.passed
      ).catch(() => { /* non-critical */ });

      return { enrollmentId: enrollment.id, pathwayId, nextModule: nextModuleInfo, totalModules, modulesCompleted: newModulesCompleted, isPathwayComplete };
    },
    onSuccess: (result) => {
      if (result) {
        setCompletionData(result);
      }
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['module-progress', moduleId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['module-progress', result?.enrollmentId] });
      queryClient.invalidateQueries({ queryKey: ['pathway-enrollment', result?.pathwayId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['pathway-progress', result?.pathwayId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-enrollments', profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['pathway-modules'] });
      toast.success('Quiz completed!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save progress');
    }
  });

  const handleAnswer = (questionId: string, answerValue: number | number[]) => {
    if (isSubmitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: answerValue }));
  };

  const handleMultiSelectToggle = (questionId: string, optionIndex: number) => {
    if (isSubmitted) return;
    
    const currentAnswers = answers[questionId];
    const currentArray = Array.isArray(currentAnswers) ? currentAnswers : [];
    
    const newAnswers = currentArray.includes(optionIndex)
      ? currentArray.filter(a => a !== optionIndex)
      : [...currentArray, optionIndex].sort();
    
    setAnswers((prev) => ({ ...prev, [questionId]: newAnswers }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    // Check all questions answered
    const unanswered = shuffledQuestions.filter((q) => {
      const answer = answers[q.id];
      if (q.question_type === 'free_text') {
        return typeof answer !== 'string' || answer.trim().length === 0;
      }
      if (q.question_type === 'multi_select') {
        return !Array.isArray(answer) || answer.length === 0;
      }
      return answer === undefined;
    });
    
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }
    setIsSubmitted(true);
    submitMutation.mutate();
  };

  const handleRetry = () => {
    setAnswers({});
    setIsSubmitted(false);
    setCurrentQuestionIndex(0);
    setShowExplanations(false);
  };

  const handleBack = () => {
    if (pathwayId) {
      navigate(`/learning/pathway/${pathwayId}`);
    } else {
      navigate('/learning/pathways');
    }
  };

  const isQuestionAnswered = (question: QuizQuestion) => {
    const answer = answers[question.id];
    if (question.question_type === 'multi_select') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer !== undefined;
  };

  const getCorrectAnswerDisplay = (question: QuizQuestion) => {
    if (question.question_type === 'free_text') return 'Free text response';
    if (question.question_type === 'multi_select' && Array.isArray(question.correct_answer)) {
      return question.correct_answer.map(i => question.options[i]).filter(Boolean).join(', ');
    }
    const idx = Array.isArray(question.correct_answer) ? question.correct_answer[0] : question.correct_answer;
    return question.options[idx];
  };

  const getUserAnswerDisplay = (question: QuizQuestion) => {
    const answer = answers[question.id];
    if (question.question_type === 'free_text') {
      return typeof answer === 'string' && answer.trim() ? answer.trim() : 'Not answered';
    }
    if (question.question_type === 'multi_select' && Array.isArray(answer)) {
      return answer.map(i => question.options[i]).filter(Boolean).join(', ');
    }
    const idx = typeof answer === 'number' ? answer : (Array.isArray(answer) ? answer[0] : undefined);
    return idx !== undefined ? question.options[idx] : 'Not answered';
  };

  const isAnswerCorrect = (question: QuizQuestion) => {
    const userAnswer = answers[question.id];

    if (question.question_type === 'free_text') {
      return typeof userAnswer === 'string' && userAnswer.trim().length > 0;
    }

    if (question.question_type === 'multi_select') {
      const correctAnswers = Array.isArray(question.correct_answer) ? question.correct_answer : [question.correct_answer];
      const userAnswers = Array.isArray(userAnswer) ? userAnswer : [];

      return correctAnswers.every(a => userAnswers.includes(a)) &&
             userAnswers.every(a => correctAnswers.includes(a)) &&
             userAnswers.length > 0;
    }

    const correctAnswer = Array.isArray(question.correct_answer) ? question.correct_answer[0] : question.correct_answer;
    return userAnswer === correctAnswer;
  };

  if (moduleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!quizConfig || !shuffledQuestions.length) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Alert variant="destructive">
          <AlertDescription>Quiz configuration not found or has no questions.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    );
  }

  // Results view
  if (isSubmitted && results) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" onClick={handleBack} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Pathway
        </Button>

        <Card className={results.passed ? 'border-green-500/50' : 'border-amber-500/50'}>
          <CardHeader className="text-center">
            <div className={`mx-auto p-4 rounded-full ${results.passed ? 'bg-green-100 dark:bg-green-950' : 'bg-amber-100 dark:bg-amber-950'} mb-4`}>
              {results.passed ? (
                <Trophy className="w-12 h-12 text-green-600" />
              ) : (
                <XCircle className="w-12 h-12 text-amber-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {results.passed ? 'Quiz Passed!' : 'Quiz Not Passed'}
            </CardTitle>
            <CardDescription>
              {results.passed
                ? 'Congratulations! You\'ve successfully completed this quiz.'
                : `You need ${quizConfig.passing_score}% to pass. Try again!`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold">{results.score}%</p>
                <p className="text-sm text-muted-foreground">Score</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{results.correct}</p>
                <p className="text-sm text-muted-foreground">Correct</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{results.total - results.correct}</p>
                <p className="text-sm text-muted-foreground">Incorrect</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="text-3xl font-bold">{results.earnedPoints}</span>
                </div>
                <p className="text-sm text-muted-foreground">of {results.totalPoints} pts</p>
              </div>
            </div>

            {/* Answer review toggle */}
            <Button
              variant="outline"
              onClick={() => setShowExplanations(!showExplanations)}
              className="w-full mb-4"
            >
              {showExplanations ? 'Hide' : 'Show'} Answer Review
            </Button>

            {/* Answer Review */}
            {showExplanations && (
              <div className="space-y-4 mb-6">
                {shuffledQuestions.map((q, idx) => {
                  const isCorrect = isAnswerCorrect(q);
                  return (
                    <div
                      key={q.id}
                      className={`p-4 rounded-lg border ${isCorrect ? 'border-green-500/30 bg-green-50/50 dark:bg-green-950/20' : 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20'}`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        {isCorrect ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">Q{idx + 1}: {q.question}</p>
                            <Badge variant="secondary" className="text-xs">
                              {q.points || 1} pt{(q.points || 1) !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <p className="text-sm mt-1">
                            Your answer: <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>{getUserAnswerDisplay(q)}</span>
                          </p>
                          {!isCorrect && (
                            <p className="text-sm text-green-600">
                              Correct answer: {getCorrectAnswerDisplay(q)}
                            </p>
                          )}
                          {q.explanation && (
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              {q.explanation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Completion Card with next module info */}
            {results.passed && completionData && pathwayId ? (
              <ModuleCompletionCard
                completedModuleTitle={module?.title || 'Quiz'}
                nextModule={completionData.nextModule}
                pathwayId={pathwayId}
                modulesCompleted={completionData.modulesCompleted}
                totalModules={completionData.totalModules}
                isPathwayComplete={completionData.isPathwayComplete}
                score={results.score}
                passed={results.passed}
              />
            ) : (
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleBack} className="flex-1">
                  Back to Pathway
                </Button>
                {!results.passed && (
                  <Button onClick={handleRetry} className="flex-1 gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Try Again
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz taking view
  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Button variant="ghost" onClick={handleBack} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Exit Quiz
      </Button>

      {/* Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                {module?.title || 'Knowledge Quiz'}
              </CardTitle>
              <CardDescription>
                Answer all questions to complete this module
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Clock className="w-3 h-3" />
                {quizConfig.passing_score}% to pass
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <Star className="w-3 h-3" />
                {totalPoints} pts
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Progress value={((currentQuestionIndex + 1) / totalQuestions) * 100} className="h-2" />
          <p className="text-sm text-muted-foreground mt-2">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </p>
        </CardContent>
      </Card>

      {/* Question */}
      {currentQuestion && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{currentQuestion.question}</h2>
              <Badge variant="secondary">
                {currentQuestion.points || 1} pt{(currentQuestion.points || 1) !== 1 ? 's' : ''}
              </Badge>
            </div>

            {currentQuestion.question_type === 'free_text' ? (
              // Free text input
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-2">Type your answer below:</p>
                <textarea
                  value={typeof answers[currentQuestion.id] === 'string' ? answers[currentQuestion.id] as string : ''}
                  onChange={(e) => setAnswers(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                  placeholder="Enter your answer here..."
                  rows={4}
                  className="w-full rounded-lg border border-muted p-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y bg-background"
                />
              </div>
            ) : currentQuestion.question_type === 'multi_select' ? (
              // Multi-select with checkboxes
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-2">Select all that apply:</p>
                {currentQuestion.options.map((option, idx) => {
                  const currentAnswers = answers[currentQuestion.id];
                  const isChecked = Array.isArray(currentAnswers) && currentAnswers.includes(idx);
                  return (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        isChecked
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/50'
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => handleMultiSelectToggle(currentQuestion.id, idx)}
                      />
                      <span className="flex-1 font-normal">
                        <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                        {option}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              // Single select with radio buttons
              <RadioGroup
                value={answers[currentQuestion.id]?.toString()}
                onValueChange={(value) => handleAnswer(currentQuestion.id, parseInt(value))}
              >
                <div className="space-y-3">
                  {currentQuestion.options.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        answers[currentQuestion.id] === idx
                          ? 'border-primary bg-primary/5'
                          : 'border-muted hover:border-muted-foreground/50'
                      }`}
                    >
                      <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                      <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer font-normal">
                        <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                        {option}
                      </Label>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <div className="flex gap-1 flex-wrap justify-center max-w-md">
          {shuffledQuestions.map((q, idx) => (
            <button
              key={q.id}
              onClick={() => setCurrentQuestionIndex(idx)}
              className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                idx === currentQuestionIndex
                  ? 'bg-primary text-primary-foreground'
                  : isQuestionAnswered(q)
                  ? 'bg-green-500 text-white'
                  : 'bg-muted hover:bg-muted-foreground/20'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {currentQuestionIndex === totalQuestions - 1 ? (
          <Button onClick={handleSubmit} disabled={submitMutation.isPending}>
            {submitMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Submit Quiz
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Answered count */}
      <p className="text-center text-sm text-muted-foreground mt-4">
        {shuffledQuestions.filter(q => isQuestionAnswered(q)).length} of {totalQuestions} answered
      </p>
    </div>
  );
}
